import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat.js";

import TradeInCard from "components/trade-in-card";
import TradeInDetails from "components/trade-in-details";
import BottomBar from "components/bottom-bar";
import ShippingProgress from "components/shipping-progress";
import { useGetTakebackDetail } from "api/tes/useGetTakebackDetail";
import stores from "consts/stores.json";
import { useAddStatus } from "api/tes/useAddStatus";
import {
  getAppointmentBySysId,
  updateAppointment,
} from "components/appointments/service";
import AuthContext from "contexts/AuthContext";
import ManagePaymentModal from "components/manage-payment-modal";
import PinEntryModal from "components/pin-entry-modal";
import AccountModal from "components/account-modal";
import { Button } from "components/button";

// Extend dayjs with plugins
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export default function TradeInDetail() {
  const { id: tradeInId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  // State for modals and appointment
  const [appointment, setAppointment] = useState(null);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showEditSellerModal, setShowEditSellerModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({ state: "" });

  // Check if this is a kiosk flow (has ?flow=kiosk in URL)
  const searchParams = new URLSearchParams(location.search);
  const isKioskFlow = searchParams.get("flow") === "kiosk";

  const { mutate: addStatus } = useAddStatus();

  // Use the detailed takeback query to get payment and processing history data
  const {
    data: takebackData,
    isLoading: isTakebackLoading,
    error: takebackError,
  } = useGetTakebackDetail(tradeInId, {
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component remount if data exists
    keepPreviousData: true, // Keep previous data while refetching to prevent flashing
  });

  const takeback = takebackData?.data?.takeback;
  const customer = takeback?.customer_id_belongsTo;

  // Fetch appointment data for both flows
  useEffect(() => {
    const fetchData = async () => {
      if (user.state) {
        setCurrentLocation({ state: user.state });
      }

      try {
        const appointmentData = await getAppointmentBySysId(tradeInId);
        setAppointment(appointmentData);
      } catch (error) {
        console.error("Error fetching appointment data:", error);
        // Don't fail if appointment isn't found - might be a dropoff flow
      }
    };

    fetchData();
  }, [tradeInId, user.state]);
  // Loading state
  if (isTakebackLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading trade-in...</div>
      </div>
    );
  }

  // Error state - only show error if we're not loading AND have an error or no data
  if (!isTakebackLoading && (takebackError || !takeback)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl text-red-600">Trade-in not found</div>
      </div>
    );
  }

  // Transform data for TradeInCard
  const data = takeback.data || {};

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getLatestStatus = () => {
    const history = takeback.takeback_processing_history_list || [];
    if (history.length === 0) return "";
    const latest = history.reduce((latest, current) =>
      current.updated_at > latest.updated_at ? current : latest
    );
    return latest.status
      .replace(/-/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const takebackName = `Trade-in ${takeback.id.slice(-6)}`;
  const customerName = customer
    ? `${customer.name || ""} ${customer.last_name || ""}`.trim()
    : "Loading...";

  const address = customer
    ? [
        customer.address_line1,
        customer.address_line2,
        customer.city,
        customer.state,
        customer.postal_code,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  // Don't render until we have takeback data
  if (!takeback) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading trade-in...</div>
      </div>
    );
  }

  // Helper functions for kiosk flow
  console.log(takeback);
  const verifiedQuantity = takeback?.data?.conform_weight || null;
  const originalEstimate =
    data.originalEstimatedWeight ||
    takeback.quantity ||
    takeback?.u_lego_presort_weight ||
    null;

  // const unit = takeback ? data.uom || data.u_lego_uom || "lbs" : "lbs";
  const takebackIsVerified =
    takeback &&
    takeback?.takeback_processing_history_list.find(
      ({ parent_status }) => parent_status === "processed"
    );
  const name = appointment
    ? `${appointment.first_name} ${appointment.last_name}`
    : customerName;

  const handleCloseModal = () => {
    navigate(t("urls.store_schedule"));
  };

  const handleStartTradeIn = () => {
    addStatus({
      id: tradeInId,
      input: {
        status: "in-store-checkin",
        parent_status: "processing",
        updated_at: parseInt(Date.now() / 1000),
      },
    });

    if (appointment?.id) {
      updateAppointment(appointment.id, { arrivalStatus: "arrived" });
    }

    navigate(t("urls.store_trade_in_weight") + "?id=" + tradeInId);
  };

  console.log(takeback);

  const storeLocation = stores.find(
    ({ value }) => value === data.store
  )?.address;

  // Render kiosk flow UI
  if (isKioskFlow) {
    return (
      <div className="fixed inset-0 bg-[#fff] overflow-y-auto w-full h-full z-[9999] flex flex-col">
        <div className="relative h-full flex flex-col mx-auto w-full bg-white rounded-none px-6 md:px-24 lg:px-40">
          {/* Header */}
          <div className="flex flex-col px-8 pt-8 pb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-bold">Trade-In</h1>
                <div className="text-gray-400 text-lg font-medium mt-1">
                  {!takebackIsVerified
                    ? "Trade-In Created"
                    : !takeback?.takeback_processing_history_list?.find(
                          ({ status }) => status === "payment-create-SUCCEEDED"
                        )
                      ? "Payment Not Issued"
                      : "Payment Issued"}
                </div>
              </div>
              {/* <button
                onClick={handleCloseModal}
                className="text-3xl text-black ml-4"
                aria-label="Close"
              >
                ×
              </button> */}
            </div>
          </div>

          <div className="px-8">
            <TradeInDetails
              customerName={name}
              address={address}
              email={customer?.email || appointment?.email}
              tradeInId={tradeInId}
              dateCreated={formatDate(takeback.created_at)}
              dateVerified={
                takebackIsVerified
                  ? formatDateTime(data.inStoreVerifiedAt)
                  : null
              }
              verifiedQuantity={`${takebackIsVerified ? verifiedQuantity : originalEstimate}`}
              originalEstimate={originalEstimate ? `${originalEstimate}` : null}
              paymentDetails={`$${data?.payment_weight ? (data.payment_weight * 4).toFixed(2) : "N/A"} LEGO® e-Gift Card`}
              location={storeLocation}
              appointment={appointment}
              isDropoffStore={["in_store_nj", "in_store_pa"].includes(
                data?.returnMethod
              )}
              currentLocation={currentLocation}
              takebackIsVerified={!!takebackIsVerified}
              showEditSeller={true}
              onEditSeller={() => setShowEditSellerModal(true)}
              showManagePayment={true}
              onManagePayment={() => setIsPinModalOpen(true)}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center px-8 pb-8 pt-4 w-full">
            <button
              className="border border-gray-300 rounded-full px-6 py-2 text-lg hover:bg-gray-100"
              onClick={handleCloseModal}
            >
              Close
            </button>

            <Button onClick={handleStartTradeIn}>Start Trade-In</Button>
          </div>
        </div>

        {/* PIN Entry Modal */}
        <PinEntryModal
          isOpen={isPinModalOpen}
          onClose={() => setIsPinModalOpen(false)}
          onSuccess={() => {
            setIsPinModalOpen(false);
            setIsPaymentModalOpen(true);
          }}
          title="Manage payment"
        />

        {/* Manage Payment Modal */}
        <ManagePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          takeback={takeback}
        />

        {/* Edit Seller Modal */}
        <AccountModal
          onClose={() => setShowEditSellerModal(false)}
          show={showEditSellerModal}
          customerId={customer?.id}
        />
      </div>
    );
  }

  // Render dropoff flow UI (existing TradeInCard)
  return (
    <div>
      <TradeInCard
        tradein={takeback}
        name={takebackName}
        customerName={customerName}
        address={address}
        email={customer?.email || ""}
        tradeInId={takeback.id}
        dateCreated={formatDate(takeback.created_at)}
        dateVerified={formatDateTime(data.inStoreVerifiedAt)}
        verifiedQuantity={data?.payment_weight}
        originalEstimate={data.originalEstimatedWeight || takeback.quantity}
        paymentDetails={`$${data?.payment_weight ? (data.payment_weight * 4).toFixed(2) : "N/A"} LEGO® e-Gift Card`}
        location={storeLocation}
        status={getLatestStatus()}
        weight={data.originalEstimatedWeight || takeback.quantity || ""}
        completed={!!data.legoValue && data.legoValue !== "0"}
        appointment={appointment}
        currentLocation={currentLocation}
        forceOpen={true}
      />
      {/* Show shipping progress for completed trade-ins */}
      <div className="px-6 pb-20 max-w-2xl mx-auto">
        <ShippingProgress takeback={takeback} />
      </div>
      <BottomBar />
    </div>
  );
}
